import { useId, useState, type CSSProperties } from 'react';
import { formatElapsed, toolStatusWord, traceSummary } from './chatActivity';
import { toolLabel } from './toolLabels';
import { useElapsed } from './useElapsed';
import type { ToolActivityEntry, TurnOutcome } from './types';

/**
 * A turn as a trace: the house thinking mark, a label that shimmers while the
 * work is live and states the duration once it is not, a wall clock beside it,
 * and the tools underneath as rows that resolve.
 *
 * Row anatomy and the grid disclosure come from the Beautiful UI harness (MIT,
 * (c) 2026 Shane Levine); everything visual is in `index.css` under
 * `.emjae-trace*`, because the arrow-js surface renders the same grammar from
 * plain HTML and the two must not drift. Change one, change both.
 *
 * Deliberately absent: any per-row detail panel. Emjae's facades are read-only
 * and the results never reach the client, so a row that expands into nothing
 * would promise something this chat does not have.
 */
export function EmjaeTrace({
  entries,
  startedAt,
  durationMs,
  outcome,
  awaiting,
  status,
}: {
  entries: ToolActivityEntry[];
  /** When the live turn began, or null once it has settled. */
  startedAt: number | null;
  durationMs?: number;
  outcome?: TurnOutcome;
  /** What a parked turn is waiting on (the Eve event kind). */
  awaiting?: string;
  /** The backend's own words for the step in flight. */
  status?: string;
}) {
  const working = startedAt !== null;
  const elapsed = useElapsed(startedAt);
  const panelId = useId();
  const [manuallyExpanded, setManuallyExpanded] = useState<boolean | null>(null);
  const expanded = manuallyExpanded ?? false;

  const label = working
    ? status || 'Working'
    : traceSummary(outcome ?? 'done', entries.length, durationMs ?? null, awaiting);

  return (
    <div
      className="emjae-trace flex w-full flex-col"
      data-emjae-trace
      data-working={working ? 'true' : 'false'}
      data-rows={entries.length > 0 ? 'true' : 'false'}
    >
      <button
        type="button"
        className="emjae-trace-toggle -mx-1.5 flex w-fit items-center gap-2 rounded-control px-1.5 py-1 text-left transition-colors hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setManuallyExpanded((current) => !(current ?? false))}
      >
        <span className="emjae-thinking" aria-hidden="true">
          <span className="emjae-thinking-field" />
          <span
            className="emjae-thinking-catch"
            style={{ '--x': '13px', '--d': '0.62s' } as CSSProperties}
          />
          <span
            className="emjae-thinking-catch"
            style={{ '--x': '28px', '--d': '1.38s' } as CSSProperties}
          />
          <span className="emjae-thinking-scan" />
        </span>
        <span
          role="status"
          className={
            working ? 'emjae-shimmer text-xs font-medium' : 'text-xs font-medium text-ink-2'
          }
        >
          {label}
        </span>
        {working && (
          // Hidden from assistive tech on purpose. The transcript around this
          // is a polite live region, and a readout that changes ten times a
          // second would be ten announcements a second. Nothing is lost: the
          // settled label states the duration in words when the turn ends.
          <span
            className="font-mono text-xs text-ink-3 tabular-nums"
            aria-hidden="true"
          >
            {formatElapsed(elapsed)}
          </span>
        )}
        {entries.length > 0 && (
          <svg
            className="emjae-trace-chevron size-3 text-ink-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        )}
      </button>

      <div
        id={panelId}
        className="emjae-trace-panel"
        data-open={expanded && entries.length > 0 ? 'true' : 'false'}
      >
        <div>
          <ol className="relative mt-1 ml-1.5 flex flex-col gap-0.5 pl-3">
            <span className="emjae-trace-spine" aria-hidden="true" />
            {entries.map((entry, index) => (
              <li
                key={`${entry.name}-${index}`}
                className="emjae-trace-row flex min-h-6 items-center gap-2 pr-1"
                style={{ '--i': index } as CSSProperties}
                data-emjae-trace-row
                // Support reads what the agent really called; the client does
                // not have to.
                title={entry.name}
              >
                <span className="emjae-trace-mark" data-status={entry.status} aria-hidden="true" />
                <span className="min-w-0 truncate text-xs text-ink-2">
                  {toolLabel(entry.name)}
                </span>
                {entry.detail && (
                  // The reason, not the status: the mark and the sr-only word
                  // carry the status, so this stays quiet ink.
                  <span className="min-w-0 shrink truncate text-xs text-ink-3" title={entry.detail}>
                    {entry.detail}
                  </span>
                )}
                <span className="sr-only">{toolStatusWord(entry.status)}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
